import { useState, useEffect } from 'react';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useNotification } from 'context/notification';
import fetchExpresso from 'utility/fetchExpresso';
import EventForm from 'components/forms/EventForm';
import moment from 'moment-timezone';
import { getRelativeTime } from 'utility/dates';
import { Page } from 'components/ui/layout';
import { useAuth } from 'context/auth';
import { useEventDispatch } from 'context/event';
import useSeasonEventSave from 'hooks/useSeasonEventSave';
import SeasonEventClose from './SeasonEventClose';

export default function EventDetails(props) {
    const eventId = props.match.params.id;
    const { auth } = useAuth();
    const hostId = auth.hostId;

    const { save, loading, confirmationDialog } = useSeasonEventSave();
    const [seasons, setSeasons] = useState([]);
    const [eventRecord, setEventRecord] = useState(null);
    const [childCount, setChildCount] = useState(0);
    const [error, setError] = useState(false);
    const [defaultValues, setDefaultValues] = useState(null);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const [readOnly, setReadOnly] = useState(false);

    // Assigned Seating
    const [hostSeatsEnabled, setHostSeatsEnabled] = useState(false);
    const [seatingCharts, setSeatingCharts] = useState([]);

    const { notify } = useNotification();
    const dispatch = useEventDispatch();

    const initializeDefaultValues = (event) => {
        setEventRecord(event);
        // Set image to null if a placeholder image is returned from the server
        let imageUrl = event.image_url;

        if (String(imageUrl).includes('placeholder')) {
            imageUrl = null;
        }

        // Convert tax rate from decimal to %
        let taxRate = event.tax_rate;
        let posTaxRate = event.pos_tax_rate;

        // Handle rounding, precision errors, and trailing 0s
        if (event.tax_rate) {
            taxRate = parseFloat((taxRate * 100).toFixed(4));
        }
        if (event.pos_tax_rate) {
            posTaxRate = parseFloat((posTaxRate * 100).toFixed(4));
        }

        setDefaultValues({
            parentMode: event.parent_mode,
            seatsClosedAt: event.seats_closed_at,
            parentEventId: event.parent_event_id,
            seasonAdmissionCapacity: event.season_admission_capacity,
            seasonAdmissionUsed: event.season_admission_used,
            seasonAdmissionRemaining: event.season_admission_remaining,
            type: event.event_type_id,
            title: event.event_title,
            eventDescHtml: event.event_desc_html || '',
            description: event.event_desc,
            image: imageUrl,
            venueName: event.address1,
            venueAddress: event.address2,
            city: event.city,
            province: event.prov_state,
            postalCode: event.postal_code,
            country: event.country,
            startDate: moment(event.start_date),
            endDate: moment(event.end_date),
            seatingChartKey: event.seats_flag ? event.seat_chart_key : null,
            timezone: event.time_zone,
            taxName: event.tax_name,
            taxRate: taxRate ? taxRate : null,
            posTaxRate: posTaxRate ? posTaxRate : null,
            orderCount: event.orderCount,
            posOrderCount: event.posOrderCount,
            createdAtMsg: `Created ${getRelativeTime(event.created_at)} by ${event.created_by}`,
            updatedAtMsg:
                `Updated ${getRelativeTime(event.updated_at)}` +
                (event.updated_by ? ` by ${event.updated_by}` : ''),
        });
    };

    useEffect(() => {
        (async () => {
            try {
                setDefaultValues(null);

                const results = await Promise.all([
                    fetchExpresso(`/apiv2/events/${eventId}`),
                    fetchExpresso(`/apiv2/events/${eventId}/payouts`),
                    fetchExpresso(`/apiv2/hosts/${hostId}/seating_charts/`),
                    fetchExpresso(`/apiv2/events/?host=${hostId}`),
                ]);

                if (results[0].status !== 200 || results[1].status !== 200) {
                    throw new Error();
                }

                const event = await results[0].json();
                const payouts = await results[1].json();
                if (results[3].ok) {
                    const events = await results[3].json();
                    setSeasons(
                        events.filter(
                            (candidate) =>
                                Number(candidate.parent_mode) === 1 &&
                                !moment(candidate.end_date)
                                    .tz(candidate.time_zone, true)
                                    .isBefore(moment()),
                        ),
                    );
                    setChildCount(
                        events.filter(
                            (candidate) => String(candidate.parent_event_id) === String(eventId),
                        ).length,
                    );
                }
                let seatingCharts = [];

                if (results[2].status === 200) {
                    const { charts } = await results[2].json();
                    seatingCharts = charts;
                }

                // Make event details read only if the event has been paid out in full
                const readOnly = payouts.some((p) => p.final_payout_flag === 1);
                setReadOnly(readOnly);

                // Set default form values so user can revert changes if needed
                initializeDefaultValues(event);
                // Assigned Seating
                setSeatingCharts(seatingCharts);
                setHostSeatsEnabled(true);
            } catch (error) {
                setError(true);
            }
        })();
    }, [eventId, hostId]);

    const handleEdit = (formData) => {
        const body = {
            ...formData,
            hostId,
            taxRate: formData.taxRate ? formData.taxRate / 100 : null,
            posTaxRate: formData.posTaxRate ? formData.posTaxRate / 100 : null,
        };
        // Sending unchanged dates would also update ticket sale dates.
        if (
            body.startDate &&
            body.endDate &&
            defaultValues.startDate.isSame(body.startDate) &&
            defaultValues.endDate.isSame(body.endDate)
        ) {
            delete body.startDate;
            delete body.endDate;
        }
        save({
            endpoint: `/apiv2/events/${eventId}`,
            method: 'PUT',
            body,
            fallback: 'There was a problem updating your event',
            onSuccess: async () => {
                notify.success(defaultValues.parentMode ? 'Season Updated' : 'Event Updated');
                setUnsavedChanges(false);
                try {
                    const response = await fetchExpresso(`/apiv2/events/${eventId}`);
                    if (!response.ok) throw new Error('Refresh failed');
                    const event = await response.json();
                    dispatch({ type: 'set_event', event });
                    initializeDefaultValues(event);
                } catch {
                    notify.warning(
                        'Your changes were saved. Reload the page to refresh event details.',
                    );
                }
            },
        });
    };

    const handleClosed = async (data) => {
        const closedEvent = {
            ...eventRecord,
            seats_closed_at: data.seats_closed_at || moment.utc().format(),
        };
        dispatch({ type: 'set_event', event: closedEvent });
        initializeDefaultValues(closedEvent);
        notify.success('Season Event Closed');
        try {
            const response = await fetchExpresso(`/apiv2/events/${eventId}`);
            if (!response.ok) throw new Error('Refresh failed');
            const event = await response.json();
            dispatch({ type: 'set_event', event });
            initializeDefaultValues(event);
        } catch {
            notify.warning('The event was closed. Reload the page to refresh its details.');
        }
    };

    if (error) {
        return (
            <Page>
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    mt={12}
                >
                    <Typography variant="h5">There was a problem finding your event</Typography>
                    <Box height={32} />
                    <Button variant="outlined" onClick={() => props.history.push('/events')}>
                        Back to Events
                    </Button>
                </Box>
            </Page>
        );
    }

    // Waiting for default values to load
    if (defaultValues === null) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" width={1} height={1}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Page width="md">
            {confirmationDialog}
            <SeasonEventClose
                event={eventRecord}
                disabled={loading || unsavedChanges}
                onClosed={handleClosed}
            />
            <EventForm
                editMode
                readOnly={readOnly}
                showTaxWarning={
                    Number(defaultValues.orderCount) > 0 || Number(defaultValues.posOrderCount) > 0
                }
                unsavedChanges={unsavedChanges}
                onFormChange={() => setUnsavedChanges(true)}
                onFormReset={() => setUnsavedChanges(false)}
                defaultValues={defaultValues}
                hostSeatsEnabled={hostSeatsEnabled}
                seatingCharts={seatingCharts}
                seasons={seasons}
                childCount={childCount}
                onSubmit={handleEdit}
                loading={loading}
            />
        </Page>
    );
}
